<script setup lang="ts">
import { ref, watchEffect, watch } from 'vue'
import { DBtn, DTextField, DList } from 'front-library'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const props = withDefaults(
  defineProps<{
    modelValue: any[]
    options: any[]
    openMultiSelect?: boolean
  }>(),
  {
    modelValue: () => [],
    options: () => [],
    openMultiSelect: false
  }
)

watch(
  () => props.openMultiSelect,
  () => {
    if (props.openMultiSelect) {
      copyItems.value = [...itemsList.value]
      copySelected.value = [...arraySelected.value]
      countCopy.value = count.value
      if (props.modelValue.length == props.options.length) {
        checkAll()
      } else {
        let itemSelected = itemsList.value.filter(
          (item: { [x: string]: any }) =>
            props.modelValue.indexOf(String(item['id'])) > -1
        )
        for (const item of itemSelected) {
          addSelected(item)
        }
      }
    }
  }
)

const searchValue = ref('')
const itemsList = ref()
watchEffect(() => {
  itemsList.value = props.options
})
const arraySelected = ref<any[]>([])
const count = ref(0)
const selected = ref()
const copyItems = ref()
const copySelected = ref()
const countCopy = ref()

const emits = defineEmits(['update:modelValue', 'change:add', 'change:close'])

const emitUpdate = (val: any) => {
  emits('update:modelValue', val)
}

const addSelected = (item: any) => {
  arraySelected.value.push(item)
  itemsList.value = filter(itemsList.value, arraySelected.value)
  count.value += 1
}

const deleteSelected = (item: any[]) => {
  itemsList.value.push(item)
  arraySelected.value = filter(arraySelected.value, itemsList.value)
  count.value -= 1
}

const filter = (items: any[], listFilter: any[]) => {
  listFilter = listFilter.map((_: { [x: string]: any }) => _['id'])
  items = items.filter(
    (item: { [x: string]: any }) => listFilter.indexOf(item['id']) == -1
  )
  return items
}

const checkAll = () => {
  arraySelected.value = []
  arraySelected.value = [...props.options]
  itemsList.value = []
  count.value = arraySelected.value?.length
}

const uncheckAll = () => {
  arraySelected.value = []
  itemsList.value = props.options
  count.value = 0
}

const saveSelection = () => {
  selected.value = arraySelected.value
  if (selected.value.length === 0) {
    emits('change:close')
  } else {
    emitUpdate(
      selected.value.map((_: { [x: string]: any }) =>
        typeof _['id'] == 'number' ? _['id'].toString() : _['id']
      )
    )
    emits('change:add')
    count.value = 0
    arraySelected.value = []
  }
}

const cancelSelection = () => {
  itemsList.value = copyItems.value
  arraySelected.value = copySelected.value
  count.value = arraySelected.value?.length
  emits('change:close')
}
</script>
<template>
  <div
    v-if="openMultiSelect"
    class="fixed z-40 inset-0 overflow-hidden bg-gray-500 bg-opacity-75 transition-opacity h-full">
    <div
      class="w-screen h-full flex justify-center flex-col items-center align-bottom overflow-hidden shadow-2xl transform transition-all px-3">
      <div class="card-container bg-white rounded-lg overflow-hidden">
        <div>
          <header
            class="sticky inset-x-0 top-0 bg-gray-100 w-full h-16 px-4 flex items-center justify-center gap-3 border">
            <h1
              class="font-bold text-xl"
              v-html="t('liquidations.components.additionalConcepts')" />
            <div
              class="flex justify-center items-center rounded-full bg-blue-600 text-white w-9 h-9 font-bold">
              {{ count }}
            </div>
          </header>
          <article>
            <div class="p-3">
              <DTextField
                :placeholder="t('form.button.search') + '...'"
                v-model="searchValue" />
            </div>
            <div class="flex flex-row items-start">
              <div
                class="w-screen h-full border-2 border-white border-r-gray-200 pt-2 pb-4 px-2 max-h-96 overflow-y-auto overflow-x-hidden">
                <a
                  class="text-gray-400 cursor-pointer underline"
                  @click.prevent="checkAll"
                  v-html="t('liquidations.components.selectAll')" />
                <DList
                  v-for="(item, index) in itemsList.filter(function (x: any) {
                    return (
                      searchValue == '' ||
                      x['name'].toUpperCase().indexOf(searchValue.toUpperCase()) > -1
                    )
                  })"
                  :key="index"
                  :nameItem="item['name']"
                  @clicked="addSelected(item)" />
              </div>
              <div
                class="w-screen h-full pt-2 pb-4 px-2 max-h-96 overflow-y-auto overflow-x-hidden">
                <a
                  class="text-gray-400 cursor-pointer underline"
                  @click.prevent="uncheckAll"
                  v-html="t('liquidations.components.deselectAll')" />
                <DList
                  v-for="(item, index) in arraySelected.filter(function (x: any) {
                    return (
                      searchValue == '' ||
                      x['name'].toUpperCase().indexOf(searchValue.toUpperCase()) > -1
                    )
                  })"
                  :key="index"
                  :nameItem="item['name']"
                  @clicked="deleteSelected(item)" />
              </div>
            </div>
          </article>
          <footer
            class="sticky inset-x-0 bottom-0 right-0 w-full flex justify-center items-center sm:justify-end h-16 gap-2 border-2 border-white border-t-gray-200 px-5">
            <DBtn
              color="secondary"
              @click="cancelSelection()"
              v-html="t('form.button.cancel')" />
            <DBtn
              @click.prevent="saveSelection"
              v-html="t('form.button.add')" />
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>
<style>
.card-container {
  width: 100vw;
  max-width: 750px;
}
</style>
